import React, { useState, useEffect } from 'react';
import { authFetch } from '../api';
import './PositionsPage.css';
import '../components/DataTable/DataTable.css';

const API_SORT_FIELDS = [
  'id',
  'token',
  'priceInUSDT',
  'pnl',
  'pnlPercent',
  'quantity',
  'totalInUSDT',
];

const COLUMN_ORDER = [
  'Date',
  'Side',
  'Platform',
  'Deposit',
  'Asset',
  'Mark Price',
  'PnL',
  'Avg Price',
  'Liquidation',
  'Quantity',
  'Total',
];

const COLUMN_TO_API_FIELD = {
  Date: 'createdDate',
  Side: 'side',
  Platform: 'platform',
  Deposit: 'depositId',
  Asset: 'token',
  'Mark Price': 'priceInUSDT',
  PnL: 'pnl',
  'Avg Price': 'avgPrice',
  Liquidation: 'liqPrice',
  Quantity: 'quantity',
  Total: 'totalInUSDT',
};

const PER_PAGE = 50;

const parseResult = (result) => {
  let list = [];
  let pageCount = 1;
  if (result && Array.isArray(result.results)) {
    list = result.results;
    if (result.pageCount) {
      pageCount = result.pageCount;
    } else if (result.total && result.currentCount) {
      pageCount = Math.max(1, Math.ceil(result.total / result.currentCount));
    }
  } else if (Array.isArray(result)) {
    list = result;
  } else if (result && Array.isArray(result.data)) {
    list = result.data;
    if (result.total) {
      pageCount = Math.max(1, Math.ceil(result.total / PER_PAGE));
    }
  }
  return { list, pageCount };
};

const PositionsPage = () => {
  const [type, setType] = useState('FUTURES');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('Date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [depositOptions, setDepositOptions] = useState([]);
  const [selectedDeposits, setSelectedDeposits] = useState([]);
  const [filterDeposits, setFilterDeposits] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [filterAssets, setFilterAssets] = useState([]);
  const [showDepositFilter, setShowDepositFilter] = useState(false);
  const [showAssetFilter, setShowAssetFilter] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const url = new URL('https://demo.api.da-capital.io/v1/trades/admin/positions');
        url.searchParams.set('type', type);
        url.searchParams.set('page', 1);
        const resp = await authFetch(url.toString(), { method: 'GET', headers: { 'Content-Type': 'application/json' } });
        if (!resp.ok) throw new Error(`HTTP error ${resp.status}`);
        const first = await resp.json();
        const { list, pageCount } = parseResult(first);
        let all = [...list];
        if (pageCount > 1) {
          const promises = [];
          for (let p = 2; p <= pageCount; p++) {
            const u = new URL(url);
            u.searchParams.set('page', p);
            promises.push(
              authFetch(u.toString(), { method: 'GET', headers: { 'Content-Type': 'application/json' } })
                .then(r => { if (!r.ok) throw new Error(`HTTP error ${r.status}`); return r.json(); })
                .then(res => parseResult(res).list)
            );
          }
          const rest = await Promise.all(promises);
          rest.forEach(arr => all.push(...arr));
        }
        const ids = Array.from(new Set(all.map(r => r.deposit?.id).filter(id => id !== undefined && id !== null)));
        const assets = Array.from(new Set(all.map(r => (r.token ? r.token.replace(/^USDT_/, '') : null)).filter(Boolean)));
        setDepositOptions(ids);
        setAssetOptions(assets);
      } catch (e) {
        console.error(e);
      }
    };
    fetchOptions();
  }, [type]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const baseUrl = new URL('https://demo.api.da-capital.io/v1/trades/admin/positions');
        baseUrl.searchParams.set('type', type);
        const apiField = COLUMN_TO_API_FIELD[sortKey];
        if (apiField && API_SORT_FIELDS.includes(apiField)) {
          baseUrl.searchParams.set('sortBy', apiField);
          baseUrl.searchParams.set('sortOrder', sortOrder);
        }
        if (filterDeposits.length) {
          baseUrl.searchParams.append('filters', `depositId=${filterDeposits.join(',')}`);
        }

        if (filterAssets.length) {
          baseUrl.searchParams.set('page', 1);
          const firstResp = await authFetch(baseUrl.toString(), { method: 'GET', headers: { 'Content-Type': 'application/json' } });
          if (!firstResp.ok) throw new Error(`HTTP error ${firstResp.status}`);
          const firstRes = await firstResp.json();
          const { list: firstList, pageCount } = parseResult(firstRes);
          let all = [...firstList];
          if (pageCount > 1) {
            const promises = [];
            for (let p = 2; p <= pageCount; p++) {
              const u = new URL(baseUrl);
              u.searchParams.set('page', p);
              promises.push(
                authFetch(u.toString(), { method: 'GET', headers: { 'Content-Type': 'application/json' } })
                  .then(r => { if (!r.ok) throw new Error(`HTTP error ${r.status}`); return r.json(); })
                  .then(res => parseResult(res).list)
              );
            }
            const rest = await Promise.all(promises);
            rest.forEach(arr => all.push(...arr));
          }
          const filtered = all.filter(r => filterAssets.includes((r.token || '').replace(/^USDT_/, '')));
          setTotalPages(Math.max(1, Math.ceil(filtered.length / PER_PAGE)));
          const start = (page - 1) * PER_PAGE;
          setData(filtered.slice(start, start + PER_PAGE));
        } else {
          baseUrl.searchParams.set('page', page);
          const resp = await authFetch(baseUrl.toString(), { method: 'GET', headers: { 'Content-Type': 'application/json' } });
          if (!resp.ok) throw new Error(`HTTP error ${resp.status}`);
          const result = await resp.json();
          const { list, pageCount } = parseResult(result);
          setData(list);
          setTotalPages(pageCount);
        }
        setError(null);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [type, sortKey, sortOrder, page, filterDeposits, filterAssets]);

const preparedData = data.map(row => ({
    Date: row.originalData?.createdDate || row.createdAt,
    Side:
      row.originalData?.side === 'Sell'
        ? '🟥 Short'
        : row.originalData?.side === 'Buy'
          ? '🟩 Long'
          : row.originalData?.side,
    Platform: row.deposit?.platform,
    Deposit: row.deposit?.id,
    Asset: row.token ? row.token.replace(/^USDT_/, '') : undefined,
    'Mark Price': row.markPrice ?? row.priceInUSDT,
    PnL: row.pnl,
    'Avg Price': row.originalData?.avgPrice ?? row.avgPrice,
    Liquidation: row.originalData?.liqPrice ?? row.liqPrice,
    Quantity: row.quantity,
    Total: row.totalInUSDT,
  }));

  const keys = COLUMN_ORDER;

  const sortedData = React.useMemo(() => {
    const apiField = COLUMN_TO_API_FIELD[sortKey];
    if (apiField && API_SORT_FIELDS.includes(apiField)) {
      return preparedData;
    }
    const arr = [...preparedData];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortOrder === 'ASC' ? av - bv : bv - av;
      }
      return sortOrder === 'ASC'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [preparedData, sortKey, sortOrder]);

  const handleSort = key => {
    if (!COLUMN_ORDER.includes(key) || ['Mark Price', 'Total'].includes(key)) {
      return;
    }
    if (sortKey === key) {
      setSortOrder(o => (o === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortKey(key);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const handleDepositChange = id => {
    setSelectedDeposits(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const handleAssetChange = asset => {
    setSelectedAssets(prev =>
      prev.includes(asset) ? prev.filter(a => a !== asset) : [...prev, asset]
    );
  };

  const applyFilters = () => {
    setFilterDeposits(selectedDeposits);
    setFilterAssets(selectedAssets);
    setPage(1);
    setShowDepositFilter(false);
    setShowAssetFilter(false);
  };

  const resetFilters = () => {
    setSelectedDeposits([]);
    setSelectedAssets([]);
    setFilterDeposits([]);
    setFilterAssets([]);
    setType('FUTURES');
    setSortKey('Date');
    setSortOrder('DESC');
    setPage(1);
    setShowDepositFilter(false);
    setShowAssetFilter(false);
  };


  return (
    <div className="positions-container">
      <div className="toggle">
          <button
            className={type === 'SPOT' ? 'active' : ''}
            onClick={() => {
              setType('SPOT');
              setPage(1);
            }}
          >
            SPOT
          </button>
          <button
            className={type === 'FUTURES' ? 'active' : ''}
            onClick={() => {
              setType('FUTURES');
              setPage(1);
            }}
          >
            FUTURES
          </button>
        </div>
      
      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <> 
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {keys.map(k => (
                  <th
                    key={k}
                    className={`${!['Mark Price', 'Total'].includes(k) ? 'sortable' : ''} ${['Deposit','Asset'].includes(k) ? 'header-filter' : ''}`}
                    onClick={() => handleSort(k)}
                  >
                    {k}
                    {['Deposit'].includes(k) && (
                      <>
                        <span
                          className="filter-icon"
                          onClick={e => { e.stopPropagation(); setShowDepositFilter(s => !s); }}
                        >🔍</span>
                        {showDepositFilter && (
                          <div className="filter-menu" onClick={e => e.stopPropagation()}>
                            {depositOptions.map(id => (
                              <label key={id}>
                                <input
                                  type="checkbox"
                                  checked={selectedDeposits.includes(id)}
                                  onChange={() => handleDepositChange(id)}
                                />{' '}
                                {id}
                              </label>
                            ))}
                            <div>
                              <button onClick={applyFilters}>SHOW</button>
                              <button onClick={resetFilters}>RESET</button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {['Asset'].includes(k) && (
                      <>
                        <span
                          className="filter-icon"
                          onClick={e => { e.stopPropagation(); setShowAssetFilter(s => !s); }}
                        >🔍</span>
                        {showAssetFilter && (
                          <div className="filter-menu" onClick={e => e.stopPropagation()}>
                            {assetOptions.map(asset => (
                              <label key={asset}>
                                <input
                                  type="checkbox"
                                  checked={selectedAssets.includes(asset)}
                                  onChange={() => handleAssetChange(asset)}
                                />{' '}
                                {asset}
                              </label>
                            ))}
                            <div>
                              <button onClick={applyFilters}>SHOW</button>
                              <button onClick={resetFilters}>RESET</button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {sortKey === k ? (sortOrder === 'ASC' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => (
                <tr key={idx}>
                  {keys.map(k => (
                    <td key={k}>{row[k] !== undefined && row[k] !== null ? String(row[k]) : ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;</button>
            <span className="page-num">{`${page} / ${totalPages}`}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&gt;</button>
          </div>
        </>
      )}
    </div>
  );
};

export default PositionsPage;
